import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  ElectricBolt as ElectricityIcon,
  Water as WaterIcon,
  LocalGasStation as GasIcon,
  Wifi as InternetIcon,
  Build as MaintenanceIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import { toast } from 'react-toastify';
import billService from '../../services/billService';

const TenantBills = () => {
  const [bills, setBills] = useState([]);
  const [totalBills, setTotalBills] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [filters, setFilters] = useState({
    bill_type: '',
    status: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchBills();
  }, [page, rowsPerPage, filters]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      
      // Build query params for server-side pagination and filtering
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      
      if (filters.bill_type) params.bill_type = filters.bill_type;
      if (filters.status) params.status = filters.status;
      
      const response = await billService.getMyBills(params);
      
      // Backend now returns {items: [], total: N, skip: N, limit: N}
      const { items, total } = response.data;
      
      setBills(Array.isArray(items) ? items : []);
      setTotalBills(total || 0);
    } catch (error) {
      console.error('Error loading bills:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load bills');
      }
      setBills([]);
      setTotalBills(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenViewDialog = (bill) => {
    setSelectedBill(bill);
    setViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setSelectedBill(null);
  };

  const getBillTypeIcon = (type) => {
    switch (type) {
      case 'electricity':
        return <ElectricityIcon fontSize="small" />;
      case 'water':
        return <WaterIcon fontSize="small" />;
      case 'gas':
        return <GasIcon fontSize="small" />;
      case 'internet':
        return <InternetIcon fontSize="small" />;
      case 'maintenance':
        return <MaintenanceIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  const getBillTypeColor = (type) => {
    switch (type) {
      case 'electricity':
        return 'warning';
      case 'water':
        return 'info';
      case 'gas':
        return 'error';
      case 'internet':
        return 'secondary';
      case 'maintenance':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'partial_paid':
        return 'info';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filtering is now done server-side
  // Calculate statistics from current page data
  const totalAmount = bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const pendingBills = bills.filter(b => b.status === 'pending').length;
  const pendingAmount = bills.filter(b => b.status === 'pending').reduce((sum, b) => sum + (b.amount || 0), 0);
  const partialPaidBills = bills.filter(b => b.status === 'partial_paid');
  const partialPaidCount = partialPaidBills.length;
  const partialPaidRemaining = partialPaidBills.reduce((sum, b) => sum + (b.remaining_balance || 0), 0);
  const paidBills = bills.filter(b => b.status === 'paid').length;
  const paidAmount = bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);
  
  // Calculate total unpaid (pending + partial paid remaining)
  const unpaidCount = pendingBills + partialPaidCount;
  const unpaidAmount = pendingAmount + partialPaidRemaining;

  if (loading) {
    return <Loading message="Loading bills..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Bills"
        subtitle="View your utility bills"
        breadcrumbs={[{ label: 'My Bills' }]}
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBills}
          >
            Refresh
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total Bills
                  </Typography>
                  <Typography variant="h4">{totalBills}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {totalAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Pending
                  </Typography>
                  <Typography variant="h4">{pendingBills}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {pendingAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Partial Paid
                  </Typography>
                  <Typography variant="h4">{partialPaidCount}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {partialPaidRemaining.toLocaleString()} remaining
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="inherit" variant="h6">
                    Not Paid
                  </Typography>
                  <Typography variant="h4" color="inherit">{unpaidCount}</Typography>
                  <Typography variant="caption" color="inherit">
                    KES {unpaidAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'error.contrastText' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Paid
                  </Typography>
                  <Typography variant="h4">{paidBills}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {paidAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Filter by Type"
              name="bill_type"
              value={filters.bill_type}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="electricity">Electricity</MenuItem>
              <MenuItem value="water">Water</MenuItem>
              <MenuItem value="gas">Gas</MenuItem>
              <MenuItem value="internet">Internet</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Filter by Status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="partial_paid">Partial Paid</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Bills Table */}
      {totalBills === 0 ? (
        <EmptyState
          icon={ReceiptIcon}
          title="No Bills Yet"
          description="You don't have any utility bills yet. Bills will appear here when your landlord creates them."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Billing Period</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Invoice No</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>
                    <Chip
                      icon={getBillTypeIcon(bill.bill_type)}
                      label={bill.bill_type.toUpperCase()}
                      color={getBillTypeColor(bill.bill_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {bill.amount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{bill.billing_period}</TableCell>
                  <TableCell>
                    {new Date(bill.due_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{bill.room_number}</TableCell>
                  <TableCell>
                    <Chip
                      label={bill.status.toUpperCase()}
                      color={getStatusColor(bill.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {bill.invoice_number ? (
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        #{bill.invoice_number}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {bill.invoice_id ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Chip
                          icon={<PaymentIcon />}
                          label="View Invoice"
                          size="small"
                          color="primary"
                          clickable
                          onClick={() => window.location.href = '/tenant/invoices'}
                        />
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleOpenViewDialog(bill)}
                        >
                          View
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Chip
                          label="Not Invoiced"
                          size="small"
                          color="default"
                        />
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleOpenViewDialog(bill)}
                        >
                          View
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
            component="div"
            count={totalBills}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* View Bill Dialog */}
      <Dialog open={viewDialog} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bill Details</DialogTitle>
        <DialogContent>
          {selectedBill && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedBill.bill_type.toUpperCase()} Bill
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Billing Period: {selectedBill.billing_period}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Room Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedBill.room_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    KES {selectedBill.amount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedBill.due_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedBill.status.toUpperCase()}
                    color={getStatusColor(selectedBill.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Created Date
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedBill.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedBill.updated_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                {selectedBill.invoice_number && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Invoice Number
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold" gutterBottom>
                      #{selectedBill.invoice_number}
                    </Typography>
                  </Grid>
                )}
                {selectedBill.invoice_id && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                      <Typography variant="body2" color="success.dark" fontWeight="bold" gutterBottom>
                        Invoice Created
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        This bill has been converted to an invoice. You can view and pay it in the "My Invoices" section.
                      </Typography>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => window.location.href = '/tenant/invoices'}
                      >
                        Go to Invoices
                      </Button>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedBill && selectedBill.status === 'pending' && !selectedBill.invoice_id && (
            <Button
              variant="outlined"
              color="info"
              disabled
            >
              Awaiting Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantBills;
